import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, GraduationCap, Briefcase, CheckCircle2, TrendingUp, Recycle, Award, UserPlus, X, Loader2, Heart, RefreshCw, Handshake, Calendar, MapPin, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { saveSubmission } from '../services/databaseService';
import { sendViaWeb3Forms } from '../services/web3formsService';
type ModalType = 'membre' | 'recruter' | 'formation' | null;
export function HomePage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [charterAccepted, setCharterAccepted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(formRef.current!);
    let submission: any = {};
    if (activeModal === 'membre') {
      submission = {
        type: 'membre' as const,
        nom: formData.get('nom') as string || '',
        prenom: formData.get('prenom') as string || '',
        telephone: formData.get('telephone') as string || '',
        email: formData.get('email') as string || '',
        ville: formData.get('ville') as string || '',
        profession: formData.get('profession') as string || '',
        typeMembre: formData.get('typeMembre') as string || '',
        contribution: formData.get('contribution') as string || '',
        motivations: formData.get('motivations') as string || ''
      };
    } else if (activeModal === 'recruter') {
      submission = {
        type: 'recruter' as const,
        nom: formData.get('nom') as string || '',
        prenom: formData.get('prenom') as string || '',
        telephone: formData.get('telephone') as string || '',
        email: formData.get('email') as string || '',
        ville: formData.get('ville') as string || '',
        quartier: formData.get('quartier') as string || '',
        typePersonnel: formData.get('typePersonnel') as string || '',
        typeContrat: formData.get('typeContrat') as string || '',
        dateDebut: formData.get('dateDebut') as string || '',
        description: formData.get('description') as string || ''
      };
    } else if (activeModal === 'formation') {
      submission = {
        type: 'formation' as const,
        nom: formData.get('nom') as string || '',
        prenom: formData.get('prenom') as string || '',
        age: formData.get('age') as string || '',
        ville: formData.get('ville') as string || '',
        quartier: formData.get('quartier') as string || '',
        telephone: formData.get('telephone') as string || '',
        whatsapp: formData.get('whatsapp') as string || '',
        email: formData.get('email') as string || '',
        typeFormation: formData.get('typeFormation') as string || '',
        motivations: formData.get('motivations') as string || '',
        charteEngagement: formData.get('charteEngagement') as string || ''
      };
    }
    setTimeout(async () => {
      try {
        saveSubmission(submission);
        // Envoyer via Web3Forms
        const formData2 = new FormData(formRef.current!);
        const fields: Record<string, string> = {};
        formData2.forEach((value, key) => {
          if (typeof value === 'string') {
            fields[key] = value;
          }
        });
        const web3Result = await sendViaWeb3Forms({
          type: activeModal!,
          fields
        });
        console.log('📧 Web3Forms result:', web3Result);
        setIsSubmitting(false);
        setIsSuccess(true);
      } catch (error) {
        console.error('Error saving submission:', error);
        setIsSubmitting(false);
        alert('Une erreur est survenue. Veuillez réessayer.');
      }
    }, 500);
  };
  const closeModal = () => {
    setActiveModal(null);
    setIsSuccess(false);
    setCharterAccepted(false);
  };
  const inputClass = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all';
  const selectClass = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all bg-white';
  const labelClass = 'text-sm font-medium text-gray-700';
  const modalConfigs = {
    membre: {
      title: "Devenir Membre de l'ADCC",
      subtitle: "Rejoignez notre communauté engagée pour l'insertion professionnelle digne",
      successMessage: "Votre demande d'adhésion a été enregistrée. Notre équipe vous contactera prochainement pour finaliser votre inscription.",
      icon: UserPlus
    },
    recruter: {
      title: 'Recruter un Personnel Formé',
      subtitle: 'Décrivez vos besoins et nous vous mettrons en relation avec des profils qualifiés',
      successMessage: 'Demande reçue ! Nous vous proposerons des profils correspondant à vos critères.',
      icon: Briefcase
    },
    formation: {
      title: "S'enregistrer pour une formation",
      subtitle: 'Inscrivez-vous à une formation professionnelle ADCC et démarrez votre parcours vers un emploi digne',
      successMessage: 'Votre inscription a bien été enregistrée ! Notre équipe vous contactera très prochainement pour confirmer votre place et vous communiquer les détails de la formation.',
      icon: GraduationCap
    }
  };
  return <Layout data-id="element-1011">
      <SEO {...pageSEO.home} data-id="element-1012" />

      {/* Hero Section */}
      <section className="relative bg-[#2C3E50] text-white py-20 md:py-32 overflow-hidden" data-id="element-1013">
        <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/jfY9WFQQPLNmCKBmbcpuEW/Femmes_Menage_Emploi_3.png')] bg-cover bg-center opacity-30" data-id="element-1014"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1015">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1016">
            {/* Main Content */}
            <div className="lg:col-span-2" data-id="element-1017">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1018">
                Développer. Valoriser.{' '}
                <span className="text-[#1E88E5]" data-id="element-1019">Insérer.</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-6 leading-relaxed" data-id="element-1020">
                L'Association Dignité, Compétences Création (ADCC) agit au Bénin
                pour l'accompagnement à la professionnalisation et l'insertion
                de personnels de maison et de services, avec pour objectif un
                accès durable à un emploi digne.
              </p>

              {/* Glassmorphism info box */}
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 mb-8 max-w-2xl border border-white/20" data-id="element-1021">
                <h3 className="font-bold text-lg mb-2" data-id="element-1022">
                  Chez ADCC, l'accompagnement pré et post insertion.
                </h3>
                <p className="text-gray-200 text-sm leading-relaxed" data-id="element-1023">
                  Nous accompagnons les bénéficiaires jusqu'à l'insertion
                  professionnelle, en facilitant leur mise en relation avec des
                  employeurs, des familles, des structures et des partenaires,
                  dans un cadre éthique, transparent et respectueux des droits
                  humains.
                </p>
              </div>

              <div className="flex flex-wrap gap-4" data-id="element-1024">
                <Button size="lg" className="text-lg px-8 py-6 bg-[#1E88E5] hover:bg-[#1976D2] gap-2" onClick={() => setActiveModal('formation')} data-id="element-1025">
                  S'enregistrer pour une formation
                  <ArrowRight className="w-5 h-5" data-id="element-1026" />
                </Button>
              </div>
            </div>

            {/* CTA Cards - Right Side */}
            <div className="space-y-4" data-id="element-1027">
              {/* Devenir Membre Card */}
              <div className="bg-white rounded-xl p-6 shadow-xl" data-id="element-1028">
                <div className="flex items-center gap-3 mb-4" data-id="element-1029">
                  <div className="p-3 bg-[#1E88E5] rounded-full" data-id="element-1030">
                    <UserPlus className="w-6 h-6 text-white" data-id="element-1031" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1032">
                    Rejoignez-nous
                  </h3>
                </div>
                <p className="text-gray-600 mb-4 text-sm" data-id="element-1033">
                  Devenez bénévole de l'ADCC et participez à notre mission pour
                  l'insertion professionnelle digne.
                </p>
                <Link to="/benevoles" data-id="element-1034">
                  <Button className="w-full gap-2" data-id="element-1035">
                    <Heart className="w-4 h-4" data-id="element-1036" />
                    Devenir bénévole
                  </Button>
                </Link>
              </div>

              {/* Recruter Card */}
              <div className="bg-gradient-to-br from-[#1E88E5] to-[#1565C0] rounded-xl p-6 shadow-xl text-white" data-id="element-1037">
                <div className="flex items-center gap-3 mb-4" data-id="element-1038">
                  <div className="p-3 bg-white/20 rounded-full" data-id="element-1039">
                    <Briefcase className="w-6 h-6 text-white" data-id="element-1040" />
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-1041">Vous recrutez ?</h3>
                </div>
                <p className="text-blue-100 mb-4 text-sm" data-id="element-1042">
                  Accédez à des personnels compétents et accompagnés pour vos
                  besoins.
                </p>
                <Button variant="ghost" className="w-full bg-white text-[#1E88E5] hover:bg-gray-100 hover:text-[#1E88E5] gap-2" onClick={() => setActiveModal('recruter')} data-id="element-1043">
                  <Users className="w-4 h-4" data-id="element-1044" />
                  Recruter un personnel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Stats Section - Updated with real numbers */}
      <section className="py-12 bg-white -mt-8 relative z-10" data-id="element-1045">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1046">
          <div className="text-center mb-8" data-id="element-1047">
            <span className="inline-block bg-amber-100 text-amber-800 px-4 py-1 rounded-full text-sm font-medium" data-id="element-1048">
              Nos objectifs 2026
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1049">
            {[{
            icon: GraduationCap,
            label: 'Personnes à accompagner',
            value: '100',
            subtitle: 'Objectif 2026'
          }, {
            icon: Briefcase,
            label: 'Taux de placement visé',
            value: '60%',
            subtitle: 'Des bénéficiaires accompagnés'
          }, {
            icon: Heart,
            label: "D'insertion éthique",
            value: '100%',
            subtitle: 'Transparents et dignes'
          }].map((stat, index) => <Card key={index} className="bg-white shadow-lg border-t-4 border-t-[#1E88E5]" data-id="element-1050">
                <CardContent className="flex items-center p-6" data-id="element-1051">
                  <div className="p-4 bg-blue-50 rounded-full mr-4" data-id="element-1052">
                    <stat.icon className="w-8 h-8 text-[#1E88E5]" data-id="element-1053" />
                  </div>
                  <div data-id="element-1054">
                    <p className="text-3xl font-bold text-gray-900" data-id="element-1055">
                      {stat.value}
                    </p>
                    <p className="text-gray-600 font-medium" data-id="element-1056">{stat.label}</p>
                    <p className="text-xs text-gray-400" data-id="element-1057">{stat.subtitle}</p>
                  </div>
                </CardContent>
              </Card>)}
          </div>
          <div className="text-center mt-6" data-id="element-1058">
            <p className="text-sm text-gray-500" data-id="element-1059">
              🌱 Notre premier forum a réuni ~10 personnels de maison pour
              cartographier les besoins
            </p>
          </div>
        </div>
      </section>

      {/* Value Chain Section - Updated with 5 pillars */}
      <section className="py-20 bg-gray-50" data-id="element-1060">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1061">
          <div className="text-center mb-12" data-id="element-1062">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1063">
              DE LA MONTÉE EN COMPÉTENCES À L'EMPLOI : UN PARCOURS COMPLET
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1064">
              Notre chaîne de valeur
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1065">
              L'ADCC développe une approche complète pour garantir un impact
              réel, mesurable et durable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12" data-id="element-1066">
            {[{
            step: '01',
            title: 'Développer',
            desc: 'Compétences techniques et humaines',
            icon: GraduationCap,
            color: 'bg-blue-50'
          }, {
            step: '02',
            title: 'Tester',
            desc: 'Validation et professionnalisation',
            icon: Award,
            color: 'bg-green-50'
          }, {
            step: '03',
            title: 'Insérer',
            desc: 'Mise en relation avec employeurs',
            icon: Briefcase,
            color: 'bg-purple-50'
          }, {
            step: '04',
            title: 'Suivre',
            desc: 'Accompagnement post-placement',
            icon: TrendingUp,
            color: 'bg-amber-50'
          }, {
            step: '05',
            title: 'Recycler',
            desc: 'Mise à jour des compétences',
            icon: Recycle,
            color: 'bg-teal-50'
          }].map((item, index) => <div key={index} className="relative" data-id="element-1067">
                <Card className="h-full bg-white hover:shadow-lg transition-shadow" data-id="element-1068">
                  <CardContent className="p-5" data-id="element-1069">
                    <div className="flex items-center justify-between mb-3" data-id="element-1070">
                      <span className="text-3xl font-bold text-gray-200" data-id="element-1071">
                        {item.step}
                      </span>
                      <div className={`p-2 ${item.color} rounded-lg`} data-id="element-1072">
                        <item.icon className="w-5 h-5 text-[#1E88E5]" data-id="element-1073" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1074">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-xs" data-id="element-1075">{item.desc}</p>
                  </CardContent>
                </Card>
                {index < 4 && <div className="hidden lg:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10" data-id="element-1076">
                    <ArrowRight className="w-4 h-4 text-[#1E88E5]" data-id="element-1077" />
                  </div>}
              </div>)}
          </div>

          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100" data-id="element-1078">
            <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1079">
              Notre engagement éthique
            </h3>
            <p className="text-lg text-gray-600 mb-6" data-id="element-1080">
              Nous œuvrons pour une insertion responsable, fondée sur :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1081">
              {['La dignité du travail', 'Le respect des personnes', 'La transparence des conditions', 'La valorisation des compétences acquises'].map((item, index) => <div key={index} className="flex items-center gap-3" data-id="element-1082">
                  <CheckCircle2 className="w-5 h-5 text-[#1E88E5] flex-shrink-0" data-id="element-1083" />
                  <span className="text-gray-700" data-id="element-1084">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section - Fixed overlap issue */}
      <section className="py-20 bg-white" data-id="element-1085">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1086">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1087">
            <div className="relative" data-id="element-1088">
              <div className="bg-gradient-to-br from-[#1E88E5]/10 via-[#2C3E50]/5 to-[#1E88E5]/10 rounded-2xl p-8 flex items-center justify-center min-h-[400px]" data-id="element-1089">
                <div className="text-center" data-id="element-1090">
                  <img src="https://cdn.magicpatterns.com/uploads/igFPFWe6QurLv1Fq8d9zkK/logo_sans_texte.png" alt="ADCC - Accompagner la croissance" className="w-48 h-48 mx-auto mb-6" data-id="element-1091" />
                  <div className="space-y-4" data-id="element-1092">
                    <div className="flex items-center justify-center gap-3 bg-white rounded-lg p-3 shadow-sm" data-id="element-1093">
                      <GraduationCap className="w-6 h-6 text-[#1E88E5]" data-id="element-1094" />
                      <span className="font-medium text-gray-700" data-id="element-1095">
                        Accompagnement à la professionalisation
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-3 bg-white rounded-lg p-3 shadow-sm" data-id="element-1096">
                      <Briefcase className="w-6 h-6 text-[#1E88E5]" data-id="element-1097" />
                      <span className="font-medium text-gray-700" data-id="element-1098">
                        Insertion accompagnée
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-3 bg-white rounded-lg p-3 shadow-sm" data-id="element-1099">
                      <RefreshCw className="w-6 h-6 text-[#1E88E5]" data-id="element-1100" />
                      <span className="font-medium text-gray-700" data-id="element-1101">
                        Recyclage des compétences
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 bg-[#1E88E5] text-white p-6 rounded-lg shadow-lg" data-id="element-1102">
                <p className="font-bold text-lg mb-2" data-id="element-1103">
                  "Des compétences avérées, des emplois dignes."
                </p>
                <p className="text-sm text-blue-100" data-id="element-1104">
                  L'impact se mesure aussi à l'accès à l'emploi.
                </p>
              </div>
            </div>

            <div data-id="element-1105">
              <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1106">
                NOTRE IMPACT
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6" data-id="element-1107">
                Renforcer des compétences ne suffit pas. Insérer durablement est
                notre priorité.
              </h2>
              <div className="space-y-6 text-lg text-gray-600" data-id="element-1108">
                <p data-id="element-1109">
                  L'ADCC s'engage à promouvoir la dignité humaine par le
                  renforcement des compétences et leur transformation en
                  opportunités d'emploi concrètes. Nous croyons que chaque
                  individu possède un potentiel unique qui ne demande qu'à être
                  révélé et valorisé sur le marché du travail.
                </p>
              </div>

              <div className="space-y-4 mt-8 mb-8" data-id="element-1110">
                {['Développement de compétences métier', 'Insertion en emploi accompagné', 'Suivi post-insertion et médiation', 'Recyclage des personnels déjà en service', 'Développement de partenariats employeurs'].map((item, index) => <div key={index} className="flex items-center gap-3" data-id="element-1111">
                    <CheckCircle2 className="w-5 h-5 text-[#1E88E5] flex-shrink-0" data-id="element-1112" />
                    <span className="text-gray-700" data-id="element-1113">{item}</span>
                  </div>)}
              </div>

              <Link to="/impact" data-id="element-1114">
                <Button className="bg-[#2C3E50] hover:bg-[#1a252f] text-white gap-2" data-id="element-1115">
                  Découvrir notre approche complète
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-gray-50" data-id="element-1116">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1117">
          <div className="text-center mb-12" data-id="element-1118">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1119">
              Ils nous font confiance
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1120">
              Nos Partenaires
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1121">
              L'ADCC s'associe à des acteurs engagés pour maximiser l'impact de
              ses actions en faveur de l'insertion professionnelle digne.
            </p>
          </div>

          {/* Premium Partner */}
          <div className="max-w-2xl mx-auto" data-id="element-1122">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative overflow-hidden" data-id="element-1123">
              {/* Premium badge */}
              <div className="absolute top-4 right-4" data-id="element-1124">
                <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm" data-id="element-1125">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-1126">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-1127" />
                  </svg>
                  Partenaire Premium
                </span>
              </div>

              <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-1128">
                <div className="flex-shrink-0" data-id="element-1129">
                  <img src="https://cdn.magicpatterns.com/uploads/c5DXfmbRapYgHk79paP5RZ/Logo_Trace_Academia_(2).png" alt="TRACE ACADEMIA - Partenaire Premium" className="h-24 w-auto" data-id="element-1130" />
                </div>
                <div className="text-center md:text-left" data-id="element-1131">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1132">
                    TRACE ACADEMIA
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1133">
                    Plateforme de formation en ligne leader en Afrique, TRACE
                    ACADEMIA rejoint l'ADCC pour renforcer les compétences des
                    personnels de maison à travers des contenus éducatifs de
                    qualité.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start" data-id="element-1134">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1135">
                      Formation digitale
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1136">
                      Certification
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-1137">
                      Contenus exclusifs
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Future partners placeholder */}
          <div className="mt-12 text-center" data-id="element-1138">
            <p className="text-gray-500 text-sm mb-4" data-id="element-1139">
              Vous souhaitez devenir partenaire de l'ADCC ?
            </p>
            <Link to="/partner" data-id="element-1140">
              <Button variant="secondary" className="gap-2" data-id="element-1141">
                <Handshake className="w-4 h-4" data-id="element-1142" />
                Proposer un partenariat
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2980B9] text-white" data-id="element-1143">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1144">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1145">
            Soutenez l'insertion professionnelle durable
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-1146">
            Votre soutien permet non seulement de développer les compétences,
            mais aussi de financer des dispositifs d'accompagnement de
            l'insertion des bénéficiaires vers un emploi durable, et de
            structurer ainsi un modèle d'aide à l'emploi éthique et responsable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1147">
            <Link to="/donate" data-id="element-1148">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 hover:text-white font-medium gap-2 w-full sm:w-auto" data-id="element-1149">
                <Heart className="w-5 h-5" data-id="element-1150" />
                Faire un don
              </Button>
            </Link>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 hover:text-white w-full sm:w-auto gap-2" onClick={() => setActiveModal('recruter')} data-id="element-1151">
              <Briefcase className="w-5 h-5" data-id="element-1152" />
              Recruter un personnel
            </Button>
          </div>
        </div>
      </section>

      {/* Modal */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" data-id="element-1153">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-8 relative max-h-[90vh] overflow-y-auto" data-id="element-1154">
            <button onClick={closeModal} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors z-10" data-id="element-1155">
              <X className="w-6 h-6" data-id="element-1156" />
            </button>

            <div className="p-8" data-id="element-1157">
              <div className="text-center mb-8" data-id="element-1158">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E88E5] rounded-full mb-4" data-id="element-1159">
                  {activeModal === 'membre' ? <UserPlus className="w-8 h-8 text-white" data-id="element-1160" /> : activeModal === 'recruter' ? <Briefcase className="w-8 h-8 text-white" data-id="element-1161" /> : <GraduationCap className="w-8 h-8 text-white" data-id="element-1162" />}
                </div>
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-1163">
                  {modalConfigs[activeModal].title}
                </h2>
                <p className="text-gray-600 mt-2" data-id="element-1164">
                  {modalConfigs[activeModal].subtitle}
                </p>
              </div>

              {isSuccess ? <div className="text-center py-12" data-id="element-1165">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1166">
                    <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-1167" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1168">
                    {activeModal === 'membre' ? 'Bienvenue dans la famille ADCC !' : activeModal === 'recruter' ? 'Demande envoyée !' : 'Inscription enregistrée !'}
                  </h3>
                  <p className="text-gray-600 mb-8" data-id="element-1169">
                    {modalConfigs[activeModal].successMessage}
                  </p>
                  <Button onClick={closeModal} data-id="element-1170">Fermer</Button>
                </div> : <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-id="element-1171">
                  {/* Formulaire Membre */}
                  {activeModal === 'membre' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1172">
                      <div className="space-y-2" data-id="element-1173">
                        <label className={labelClass} data-id="element-1174">Nom complet *</label>
                        <input type="text" required className={inputClass} name="nom" placeholder="Votre nom" data-id="element-1175" />
                      </div>
                      <div className="space-y-2" data-id="element-1176">
                        <label className={labelClass} data-id="element-1177">Prénom *</label>
                        <input type="text" required className={inputClass} name="prenom" placeholder="Votre prénom" data-id="element-1178" />
                      </div>
                      <div className="space-y-2" data-id="element-1179">
                        <label className={labelClass} data-id="element-1180">Téléphone *</label>
                        <input type="tel" required className={inputClass} name="telephone" placeholder="+229..." data-id="element-1181" />
                      </div>
                      <div className="space-y-2" data-id="element-1182">
                        <label className={labelClass} data-id="element-1183">Email *</label>
                        <input type="email" required className={inputClass} name="email" placeholder="votre@email.com" data-id="element-1184" />
                      </div>
                      <div className="space-y-2" data-id="element-1185">
                        <label className={labelClass} data-id="element-1186">Ville / Commune *</label>
                        <input type="text" required className={inputClass} name="ville" placeholder="Ex: Cotonou" data-id="element-1187" />
                      </div>
                      <div className="space-y-2" data-id="element-1188">
                        <label className={labelClass} data-id="element-1189">Profession</label>
                        <input type="text" className={inputClass} name="profession" placeholder="Votre profession actuelle" data-id="element-1190" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1191">
                        <label className={labelClass} data-id="element-1192">Type de membre *</label>
                        <select required className={selectClass} name="typeMembre" data-id="element-1193">
                          <option value="" data-id="element-1194">Sélectionnez...</option>
                          <option value="actif" data-id="element-1195">Membre actif</option>
                          <option value="sympathisant" data-id="element-1196">Sympathisant</option>
                          <option value="donateur" data-id="element-1197">Donateur</option>
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1198">
                        <label className={labelClass} data-id="element-1199">
                          Contribution souhaitée
                        </label>
                        <textarea className={inputClass} rows={3} name="contribution" placeholder="Comment souhaitez-vous aider ? (Temps, réseau, dons...)" data-id="element-1200"></textarea>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1201">
                        <label className={labelClass} data-id="element-1202">Motivations *</label>
                        <textarea required className={inputClass} rows={3} name="motivations" placeholder="Pourquoi souhaitez-vous rejoindre l'ADCC ?" data-id="element-1203"></textarea>
                      </div>
                    </div>}

                  {/* Formulaire Recruter */}
                  {activeModal === 'recruter' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1204">
                      <div className="space-y-2" data-id="element-1205">
                        <label className={labelClass} data-id="element-1206">Nom complet *</label>
                        <input type="text" required className={inputClass} name="nom" placeholder="Votre nom" data-id="element-1207" />
                      </div>
                      <div className="space-y-2" data-id="element-1208">
                        <label className={labelClass} data-id="element-1209">Prénom *</label>
                        <input type="text" required className={inputClass} name="prenom" placeholder="Votre prénom" data-id="element-1210" />
                      </div>
                      <div className="space-y-2" data-id="element-1211">
                        <label className={labelClass} data-id="element-1212">Téléphone *</label>
                        <input type="tel" required className={inputClass} name="telephone" placeholder="+229..." data-id="element-1213" />
                      </div>
                      <div className="space-y-2" data-id="element-1214">
                        <label className={labelClass} data-id="element-1215">Email *</label>
                        <input type="email" required className={inputClass} name="email" placeholder="votre@email.com" data-id="element-1216" />
                      </div>
                      <div className="space-y-2" data-id="element-1217">
                        <label className={labelClass} data-id="element-1218">Ville / Commune *</label>
                        <input type="text" required className={inputClass} name="ville" placeholder="Ex: Cotonou" data-id="element-1219" />
                      </div>
                      <div className="space-y-2" data-id="element-1220">
                        <label className={labelClass} data-id="element-1221">Quartier *</label>
                        <input type="text" required className={inputClass} name="quartier" placeholder="Ex: Haie Vive" data-id="element-1222" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1223">
                        <label className={labelClass} data-id="element-1224">
                          Type de personnel recherché *
                        </label>
                        <select required className={selectClass} name="typePersonnel" data-id="element-1225">
                          <option value="" data-id="element-1226">Sélectionnez...</option>
                          <option value="maison" data-id="element-1227">Personnel de maison</option>
                          <option value="hotellerie" data-id="element-1228">
                            Hôtellerie / Restauration
                          </option>
                          <option value="aide" data-id="element-1229">Aide à la personne</option>
                          <option value="autre" data-id="element-1230">Autre</option>
                        </select>
                      </div>
                      <div className="space-y-2" data-id="element-1231">
                        <label className={labelClass} data-id="element-1232">
                          Type de contrat souhaité *
                        </label>
                        <select required className={selectClass} name="typeContrat" data-id="element-1233">
                          <option value="" data-id="element-1234">Sélectionnez...</option>
                          <option value="cdi" data-id="element-1235">CDI</option>
                          <option value="cdd" data-id="element-1236">CDD</option>
                          <option value="ponctuel" data-id="element-1237">Mission ponctuelle</option>
                        </select>
                      </div>
                      <div className="space-y-2" data-id="element-1238">
                        <label className={labelClass} data-id="element-1239">
                          Date de début souhaitée *
                        </label>
                        <input type="date" required className={inputClass} name="dateDebut" data-id="element-1240" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1241">
                        <label className={labelClass} data-id="element-1242">
                          Description du besoin *
                        </label>
                        <textarea required className={inputClass} rows={4} name="description" placeholder="Détaillez les tâches, horaires, et profil recherché..." data-id="element-1243"></textarea>
                      </div>
                    </div>}

                  {/* Formulaire Formation */}
                  {activeModal === 'formation' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1244">
                      <div className="space-y-2" data-id="element-1245">
                        <label className={labelClass} data-id="element-1246">Nom *</label>
                        <input type="text" required className={inputClass} name="nom" placeholder="Votre nom" data-id="element-1247" />
                      </div>
                      <div className="space-y-2" data-id="element-1248">
                        <label className={labelClass} data-id="element-1249">Prénom *</label>
                        <input type="text" required className={inputClass} name="prenom" placeholder="Votre prénom" data-id="element-1250" />
                      </div>
                      <div className="space-y-2" data-id="element-1251">
                        <label className={labelClass} data-id="element-1252">Âge *</label>
                        <input type="number" required min="16" max="65" className={inputClass} name="age" placeholder="Votre âge" data-id="element-1253" />
                      </div>
                      <div className="space-y-2" data-id="element-1254">
                        <label className={labelClass} data-id="element-1255">Ville *</label>
                        <input type="text" required className={inputClass} name="ville" placeholder="Ex: Cotonou" data-id="element-1256" />
                      </div>
                      <div className="space-y-2" data-id="element-1257">
                        <label className={labelClass} data-id="element-1258">Quartier *</label>
                        <input type="text" required className={inputClass} name="quartier" placeholder="Ex: Haie Vive" data-id="element-1259" />
                      </div>
                      <div className="space-y-2" data-id="element-1260">
                        <label className={labelClass} data-id="element-1261">Téléphone *</label>
                        <input type="tel" required className={inputClass} name="telephone" placeholder="+229..." data-id="element-1262" />
                      </div>
                      <div className="space-y-2" data-id="element-1263">
                        <label className={labelClass} data-id="element-1264">WhatsApp</label>
                        <input type="tel" className={inputClass} name="whatsapp" placeholder="+229... (si différent)" data-id="element-1265" />
                      </div>
                      <div className="space-y-2" data-id="element-1266">
                        <label className={labelClass} data-id="element-1267">Email</label>
                        <input type="email" className={inputClass} name="email" placeholder="votre@email.com" data-id="element-1268" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1269">
                        <label className={labelClass} data-id="element-1270">
                          Type de formation souhaitée *
                        </label>
                        <select required className={selectClass} name="typeFormation" data-id="element-1271">
                          <option value="" data-id="element-1272">Sélectionnez...</option>
                          <option value="aide_menager" data-id="element-1273">Aide-ménager(e)</option>
                          <option value="agent_entretien" data-id="element-1274">
                            Agent(e) d'entretien
                          </option>
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1275">
                        <label className={labelClass} data-id="element-1276">
                          Quelle est votre motivation ? *
                        </label>
                        <textarea required className={inputClass} rows={3} name="motivations" placeholder="Décrivez brièvement ce qui vous motive à suivre cette formation..." data-id="element-1277"></textarea>
                      </div>
                      {/* Charte d'engagement du bénéficiaire */}
                      <div className="md:col-span-2 space-y-4 bg-blue-50 p-6 rounded-xl border border-blue-100" data-id="element-1278">
                        <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-1279">
                          <Shield className="w-5 h-5 text-[#1E88E5]" data-id="element-1280" />
                          Charte d'engagement du bénéficiaire ADCC
                        </h3>
                        <div className="h-48 overflow-y-auto text-sm text-gray-600 bg-white p-4 rounded border border-gray-200 space-y-4" data-id="element-1281">
                          <p className="font-bold text-gray-800" data-id="element-1282">Préambule</p>
                          <p data-id="element-1283">
                            La présente Charte d'Engagement formalise la
                            relation de confiance et de co-responsabilité entre
                            ADCC et chaque personne bénéficiaire de ses
                            formations et accompagnements.
                          </p>
                          <p data-id="element-1284">
                            Chez ADCC, nous croyons profondément que chaque
                            femme, quelle que soit son parcours, porte en elle
                            la capacité de progresser, de se professionnaliser
                            et de construire un emploi durable et reconnu.
                          </p>
                          <p data-id="element-1285">
                            Cette charte est un engagement mutuel : ADCC
                            s'engage à vous accompagner avec bienveillance,
                            exigence et respect. En retour, nous vous invitons à
                            vous engager pleinement dans cette aventure, pour
                            vous-même et pour celles qui viendront après vous.
                          </p>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1286">
                            1. Assiduité & Ponctualité
                          </p>
                          <p data-id="element-1287">
                            La régularité est la première marque de votre
                            engagement envers vous-même. Elle prépare votre
                            posture professionnelle et montre que vous prenez
                            votre formation au sérieux.
                          </p>
                          <p className="font-medium" data-id="element-1288">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1289">
                            <li data-id="element-1290">
                              ✦ Participer à l'ensemble des sessions de
                              formation prévues dans mon programme, sans
                              exception.
                            </li>
                            <li data-id="element-1291">
                              ✦ Arriver à l'heure et prévenir mon référent(e)
                              ADCC en cas d'empêchement, au plus tôt.
                            </li>
                            <li data-id="element-1292">
                              ✦ Justifier toute absence et, dans la mesure du
                              possible, rattraper les contenus manqués.
                            </li>
                            <li data-id="element-1293">
                              ✦ Respecter les horaires et les délais fixés pour
                              les travaux et exercices demandés.
                            </li>
                            <li data-id="element-1294">
                              ✦ Informer ADCC de tout changement de situation
                              susceptible d'affecter ma participation.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1295">
                            2. Formation Théorique & Pratique — Engagement à 100
                            %
                          </p>
                          <p data-id="element-1296">
                            La formation ADCC combine modules théoriques et
                            mises en pratique concrètes. Ces deux dimensions
                            sont indissociables : l'une sans l'autre ne permet
                            pas d'atteindre la professionnalisation visée.
                          </p>
                          <p className="font-medium" data-id="element-1297">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1298">
                            <li data-id="element-1299">
                              ✦ Suivre l'intégralité des modules de formation
                              théorique inscrits à mon programme, sans
                              exception.
                            </li>
                            <li data-id="element-1300">
                              ✦ Participer activement à toutes les séances
                              pratiques, ateliers et mises en situation prévus.
                            </li>
                            <li data-id="element-1301">
                              ✦ Préparer les séances en amont lorsque cela est
                              demandé et restituer les exercices dans les
                              délais.
                            </li>
                            <li data-id="element-1302">
                              ✦ M'investir avec sérieux et curiosité dans chaque
                              apprentissage, même lorsque la matière est
                              difficile.
                            </li>
                            <li data-id="element-1303">
                              ✦ Solliciter l'aide de mon référent(e) ADCC dès
                              que je rencontre un obstacle, sans attendre.
                            </li>
                            <li data-id="element-1304">
                              ✦ Travailler activement à ma confiance en moi : ma
                              réussite commence dans ma propre tête.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1305">
                            3. Stage de Certification
                          </p>
                          <p data-id="element-1306">
                            Le stage de certification est une étape clé : il
                            valide vos acquis en situation réelle et constitue
                            le tremplin vers votre insertion professionnelle
                            durable.
                          </p>
                          <p className="font-medium" data-id="element-1307">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1308">
                            <li data-id="element-1309">
                              ✦ Effectuer l'intégralité du stage de
                              certification dans les conditions et délais fixés
                              par ADCC.
                            </li>
                            <li data-id="element-1310">
                              ✦ Adopter en stage une posture professionnelle
                              exemplaire : ponctualité, sérieux, respect,
                              initiative.
                            </li>
                            <li data-id="element-1311">
                              ✦ Rendre compte régulièrement de mon expérience à
                              mon référent(e) ADCC tout au long du stage.
                            </li>
                            <li data-id="element-1312">
                              ✦ Remettre tous les documents requis (rapport,
                              évaluations, feuilles de présence) dans les délais
                              impartis.
                            </li>
                            <li data-id="element-1313">
                              ✦ Aborder cette étape comme une opportunité de
                              démontrer mes compétences et de créer des liens
                              professionnels durables.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1314">
                            4. Respect & Posture Professionnelle
                          </p>
                          <p data-id="element-1315">
                            Se professionnaliser, c'est adopter une posture
                            respectueuse envers les autres et envers soi-même.
                            C'est une compétence clé, aussi importante que les
                            savoir-faire techniques.
                          </p>
                          <p className="font-medium" data-id="element-1316">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1317">
                            <li data-id="element-1318">
                              ✦ Traiter avec respect et bienveillance les
                              formatrices, référentes et autres participant(e)s.
                            </li>
                            <li data-id="element-1319">
                              ✦ Adopter un comportement professionnel lors des
                              formations, ateliers et mises en situation.
                            </li>
                            <li data-id="element-1320">
                              ✦ Contribuer positivement à la dynamique du groupe
                              et encourager les autres dans leur parcours.
                            </li>
                            <li data-id="element-1321">
                              ✦ Exprimer mes désaccords ou difficultés par la
                              parole, dans un cadre constructif et bienveillant.
                            </li>
                            <li data-id="element-1322">
                              ✦ Respecter les locaux, le matériel et les
                              ressources mis à ma disposition.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1323">
                            5. Insertion Professionnelle & Suivi du Contrat de
                            Travail
                          </p>
                          <p data-id="element-1324">
                            L'insertion dans un emploi durable est l'objectif
                            central de l'accompagnement ADCC. Pour garantir les
                            meilleures conditions d'entrée et de maintien dans
                            l'emploi, je confie à ADCC un rôle actif à mes
                            côtés.
                          </p>
                          <p className="font-medium" data-id="element-1325">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1326">
                            <li data-id="element-1327">
                              ✦ Autoriser ADCC à m'accompagner dans la rédaction
                              et la négociation des clauses de mon contrat de
                              travail avec l'employeur, afin de protéger mes
                              droits et de sécuriser mon insertion.
                            </li>
                            <li data-id="element-1328">
                              ✦ Participer aux rencontres tripartites
                              (bénéficiaire — employeur — ADCC) prévues pour la
                              mise en place et le suivi du contrat.
                            </li>
                            <li data-id="element-1329">
                              ✦ Donner mon accord explicite à ADCC pour
                              contacter mon employeur dans le cadre du suivi
                              trimestriel, afin d'évaluer mon intégration et de
                              prévenir toute difficulté.
                            </li>
                            <li data-id="element-1330">
                              ✦ Participer aux évaluations régulières de ma
                              progression organisées par ADCC en cours de
                              contrat.
                            </li>
                            <li data-id="element-1331">
                              ✦ Informer sans délai mon référent(e) ADCC de
                              toute difficulté rencontrée dans mon emploi
                              (relations, conditions de travail, respect du
                              contrat).
                            </li>
                            <li data-id="element-1332">
                              ✦ Maintenir une communication ouverte et honnête
                              avec ADCC tout au long de mon contrat, dans une
                              logique de co-construction de mon parcours
                              professionnel.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1333">
                            6. Épargne Personnelle & Fonds d'Avenir
                          </p>
                          <p data-id="element-1334">
                            ADCC accompagne chaque bénéficiaire dans la
                            construction de sa stabilité financière. Au-delà de
                            l'emploi, il s'agit de préparer l'avenir avec
                            méthode et confiance. Le Fonds d'Épargne Personnelle
                            est un outil concret pour y parvenir.
                          </p>
                          <p className="font-medium mt-2" data-id="element-1335">
                            Mon Fonds d'Épargne Personnelle ADCC
                          </p>
                          <p data-id="element-1336">
                            Dès l'entrée en emploi, je m'engage à verser chaque
                            mois 5 % de mon salaire net dans le Fonds de
                            Solidarité ADCC. Cette épargne solidaire est
                            destinée exclusivement à financer l'accompagnement
                            de futures bénéficiaires qui, comme moi, ont besoin
                            d'un soutien pour accéder à un emploi durable et
                            reconnu.
                          </p>
                          <p className="font-medium" data-id="element-1337">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1338">
                            <li data-id="element-1339">
                              ✦ Verser 5 % de mon salaire net mensuel dans mon
                              Fonds d'Épargne Personnelle ADCC, dès la réception
                              de mon premier salaire.
                            </li>
                            <li data-id="element-1340">
                              ✦ Alimenter régulièrement mon épargne tout au long
                              de mon adhésion à ADCC, sans interruption.
                            </li>
                            <li data-id="element-1341">
                              ✦ Participer aux échanges proposés par ADCC sur la
                              gestion de mon budget et l'organisation de mon
                              épargne personnelle.
                            </li>
                            <li data-id="element-1342">
                              ✦ Informer ADCC de tout changement de situation
                              financière (perte d'emploi, modification de
                              contrat) susceptible d'affecter mes versements.
                            </li>
                            <li data-id="element-1343">
                              ✦ Respecter la règle de retrait : je ne peux
                              retirer tout ou partie de mon épargne qu'une fois
                              par an, à l'échéance annuelle fixée avec ADCC.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1344">
                            7. Confidentialité & Éthique
                          </p>
                          <p data-id="element-1345">
                            La confiance est le ciment de notre accompagnement.
                            Ce qui se partage au sein des groupes ADCC reste
                            dans ce cadre protégé. Vous en êtes la gardienne,
                            comme nous le sommes pour vous.
                          </p>
                          <p className="font-medium" data-id="element-1346">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1347">
                            <li data-id="element-1348">
                              ✦ Respecter la confidentialité des informations
                              personnelles partagées par d'autres
                              participant(e)s.
                            </li>
                            <li data-id="element-1349">
                              ✦ Ne pas divulguer à l'extérieur les témoignages,
                              situations ou difficultés évoqués en groupe.
                            </li>
                            <li data-id="element-1350">
                              ✦ Utiliser les ressources et supports pédagogiques
                              fournis uniquement dans le cadre de ma formation.
                            </li>
                            <li data-id="element-1351">
                              ✦ Signaler à ADCC toute situation qui me
                              semblerait contraire à l'éthique ou au respect des
                              personnes.
                            </li>
                            <li data-id="element-1352">
                              ✦ Agir avec intégrité dans toutes mes démarches
                              liées à mon parcours de formation et d'insertion.
                            </li>
                          </ul>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1353">
                            8. Droit à l'Image
                          </p>
                          <p data-id="element-1354">
                            Dans le cadre de mes activités au sein d'ADCC
                            (formations, ateliers, foires, événements et toute
                            autre manifestation organisée ou co-organisée par
                            ADCC), des photos et vidéos sont susceptibles d'être
                            réalisées à des fins de communication
                            institutionnelle et promotionnelle.
                          </p>
                          <p className="font-medium" data-id="element-1355">Je m'engage à :</p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1356">
                            <li data-id="element-1357">
                              ✦ Autoriser ADCC à utiliser mon image
                              (photographies et enregistrements vidéo) pris lors
                              des activités auxquelles je participe, à titre
                              gratuit et sans limitation de durée.
                            </li>
                            <li data-id="element-1358">
                              ✦ Accepter que ces contenus soient diffusés sur le
                              site internet adcc.africa, les réseaux sociaux
                              officiels d'ADCC et tout support de communication
                              numérique ou imprimé lié à ses activités.
                            </li>
                          </ul>
                          <p className="italic text-gray-500 text-xs mt-1" data-id="element-1359">
                            Cette autorisation peut être retirée à tout moment
                            par demande écrite adressée à ADCC, sans effet
                            rétroactif sur les contenus déjà publiés.
                          </p>

                          <p className="font-bold text-gray-800 mt-4" data-id="element-1360">
                            Les engagements d'ADCC envers vous
                          </p>
                          <p data-id="element-1361">
                            En contrepartie de votre engagement, ADCC s'engage à
                            :
                          </p>
                          <ul className="list-none space-y-1 pl-2" data-id="element-1362">
                            <li data-id="element-1363">
                              ✦ Vous accueillir avec respect, sans jugement,
                              quelle que soit votre situation de départ.
                            </li>
                            <li data-id="element-1364">
                              ✦ Vous proposer un accompagnement de qualité,
                              adapté à vos besoins et à votre rythme.
                            </li>
                            <li data-id="element-1365">
                              ✦ Vous accompagner dans la rédaction de votre
                              contrat et protéger vos droits auprès de
                              l'employeur.
                            </li>
                            <li data-id="element-1366">
                              ✦ Assurer un suivi trimestriel avec votre
                              employeur pour prévenir les difficultés et
                              consolider votre emploi.
                            </li>
                            <li data-id="element-1367">
                              ✦ Vous évaluer régulièrement en cours de contrat
                              et célébrer chacun de vos progrès.
                            </li>
                            <li data-id="element-1368">
                              ✦ Garantir la transparence et la bonne gestion du
                              Fonds de Solidarité au bénéfice de toutes.
                            </li>
                            <li data-id="element-1369">
                              ✦ Garantir la confidentialité de vos informations
                              personnelles et de votre parcours.
                            </li>
                          </ul>
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer mt-4" data-id="element-1370">
                          <input type="checkbox" name="charteEngagement" value="oui" required checked={charterAccepted} onChange={e => setCharterAccepted(e.target.checked)} className="mt-1 w-5 h-5 text-[#1E88E5] border-gray-300 rounded focus:ring-[#1E88E5]" data-id="element-1371" />
                          <span className="text-sm font-medium text-gray-900" data-id="element-1372">
                            J'ai lu et j'accepte la charte d'engagement du
                            bénéficiaire ADCC *
                          </span>
                        </label>
                      </div>
                    </div>}

                  <div className="pt-4 border-t border-gray-100" data-id="element-1373">
                    <Button type="submit" className="w-full" size="lg" disabled={isSubmitting || activeModal === 'formation' && !charterAccepted} data-id="element-1374">
                      {isSubmitting ? <span className="flex items-center justify-center gap-2" data-id="element-1375">
                          <Loader2 className="w-5 h-5 animate-spin" data-id="element-1376" />
                          Envoi en cours...
                        </span> : 'Envoyer la demande'}
                    </Button>
                  </div>
                </form>}
            </div>
          </div>
        </div>}
    </Layout>;
}